import {regexMatchHighlight} from '@wandb/weave/common/util/fuzzyMatch';
import React, {createContext, useMemo, useRef, useState} from 'react';

import {useIsTextOverflowing} from '../../../util/hooks/useIsTextOverflowing';
import {useSearchQuery} from './search';
import {
  REGISTRY_PROJECT_PREFIX,
  REGISTRY_SEARCH_HIGHLIGHT_STYLE,
} from './utils';

export type RegistriesSearchContextType = {
  searchFilter: string | undefined;
  setSearchFilter: (searchFilter: string | undefined) => void;
  searchFilterRegex: RegExp | undefined;
  searchPattern: string;
};

export const SingleRegistrySearchContext =
  createContext<RegistriesSearchContextType>({
    searchFilter: undefined,
    setSearchFilter: () => {},
    searchFilterRegex: undefined,
    searchPattern: '',
  });

// Single registry search context for (single) registry overview and/or model registry overview pages
export const SingleRegistrySearchContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({children}) => {
  // Note: Unlike the registries homepage, the single-/model-registry overview page
  // doesn't modify the URL with search params.  This can be revisited in the future.

  const [searchFilter, setSearchFilter] = useState<string | undefined>(
    undefined
  );

  const searchFilterRegex = useMemo(() => {
    let searchFilterRegex = undefined;
    try {
      if (searchFilter) {
        searchFilterRegex = new RegExp(searchFilter, 'i');
      }
    } catch (error) {
      console.warn('Error parsing single registry search filter: ', error);
    }
    return searchFilterRegex;
  }, [searchFilter]);

  const searchPattern = useMemo(() => {
    return searchFilter ? searchFilter : '';
  }, [searchFilter]);

  const value = useMemo(
    () => ({
      searchFilter,
      setSearchFilter,
      searchFilterRegex,
      searchPattern,
    }),
    [searchFilter, setSearchFilter, searchFilterRegex, searchPattern]
  );

  return (
    <SingleRegistrySearchContext.Provider value={value}>
      {children}
    </SingleRegistrySearchContext.Provider>
  );
};

// Registries homepage search context
export const RegistriesSearchContext =
  createContext<RegistriesSearchContextType>({
    searchFilter: undefined,
    setSearchFilter: () => {},
    searchFilterRegex: undefined,
    searchPattern: REGISTRY_PROJECT_PREFIX,
  });

interface RegistriesSearchContextProviderProps {
  children?: React.ReactNode;
}

export const RegistriesSearchContextProvider: React.FC<
  RegistriesSearchContextProviderProps
> = ({children}) => {
  const [searchFilter, setSearchFilter] = useSearchQuery();

  const searchFilterRegex = useMemo(() => {
    let searchFilterRegex = undefined;
    try {
      if (searchFilter) {
        searchFilterRegex = new RegExp(searchFilter, 'i');
      }
    } catch (error) {
      console.warn('Error parsing registries search filter: ', error);
    }
    return searchFilterRegex;
  }, [searchFilter]);

  const searchPattern = useMemo(() => {
    return searchFilter
      ? `${REGISTRY_PROJECT_PREFIX}%${searchFilter}`
      : REGISTRY_PROJECT_PREFIX;
  }, [searchFilter]);

  const value = useMemo(
    () => ({
      searchFilter,
      setSearchFilter,
      searchFilterRegex,
      searchPattern,
    }),
    [searchFilter, setSearchFilter, searchFilterRegex, searchPattern]
  );

  return (
    <RegistriesSearchContext.Provider value={value}>
      {children}
    </RegistriesSearchContext.Provider>
  );
};

type DisplayMatchedTextProps = {
  text: string;
  searchFilterRegex: RegExp;
};

// This component is used to intelligently truncate and highlight text
// If the matched text will show in the container, we truncate the end
// Otherwise, we truncate the middle to show the matched text
export const DisplayMatchedText = ({
  text,
  searchFilterRegex,
}: DisplayMatchedTextProps) => {
  // get the first match to figure out truncation
  const match = searchFilterRegex.exec(text);
  const matchedText = match ? match[0] : '';
  const firstPiece = match?.index ? text.slice(0, match?.index) : '';
  const remainingText =
    match?.index !== undefined
      ? text.slice(match.index + matchedText.length)
      : '';

  // check if the highlighted text would overflow the container
  // we add some characters to the end of the string to account for the ellipsis
  const truncatedText = firstPiece + matchedText + remainingText.slice(0, 4);
  const parentDomRef = useRef<HTMLDivElement | null>(null);
  const isTextOverflowing = useIsTextOverflowing({
    text: truncatedText,
    parentDomRef,
  });

  if (!matchedText) {
    return <span>{text}</span>;
  }

  const highlightedText = regexMatchHighlight(
    matchedText + remainingText,
    searchFilterRegex,
    REGISTRY_SEARCH_HIGHLIGHT_STYLE
  );

  // if the matched text doesn't overflow, we truncate the end
  const truncateEnd = (
    <>
      <span>{firstPiece}</span>
      <span className="truncate">{highlightedText}</span>
    </>
  );

  // if the matched text overflows, we truncate the middle to show the matched text
  const truncateMiddle = (
    <>
      <span className="truncate">{firstPiece}</span>
      <span
        className="shrink-0 grow truncate"
        style={{
          // add some buffer to show at least a few characters of the prefix if it exists
          maxWidth: `calc(100% - ${Math.min(firstPiece.length, 10)}ch)`,
        }}>
        {highlightedText}
      </span>
    </>
  );

  return (
    <div className="flex w-full" ref={parentDomRef}>
      {isTextOverflowing ? truncateMiddle : truncateEnd}
    </div>
  );
};
